/**
 * Resubmittals, inside Manifest Review (Isaiah Cotto, 2026-10-01).
 *
 * A Patient Advocate who decides a resident should be seen again by the
 * provider -- re-evaluated this year, re-evaluated after 12+ months, or
 * resubmitted -- closes the PA case with that word. The resident lands here,
 * once, under the building they live in now, with the next visit and the
 * provider beside the building's name.
 *
 * The VA adds the resident to that building's manifest workbook by hand (this
 * screen writes nothing to SharePoint), then selects them here and marks them
 * added. A row that should not go back is cancelled, with a reason.
 *
 * Also exported: `DispositionAction`, the "Disposition…" control the lead
 * rows in Leads to place and Unassigned carry (R5). It closes the lead with
 * the PA's own words through the PA's own status change.
 *
 * As a board (2026-10-07): one table row per resubmittal, on the same kit as
 * the other Manifest Review tabs. The row opens DOCKED beside the table, where
 * the resident is marked added or cancelled; marking many added is above the
 * table, for the residents of one building. A reason is its PA word on the row,
 * never a colour alone.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MfLoadFailure, MfSkeleton } from './mfStates';
import { StateBlock as TideState } from '../../components/tide';
import { manifestApi, type ResidentFindHit } from './manifestApi';
import {
  DISPOSITION_WORDS, RESUBMITTAL_REASON_LABEL, actKey, refusalWords, resubmittalsApi,
  type DispositionWord, type NextVisit, type Resubmittal, type ResubmittalList, type ResubmittalReason,
} from './resubmittalsApi';
import MfBoard, { useClientSort, useOpenParam, type BoardColumn, type BoardDetailSection } from './board/MfBoard';
import MfLegend, { type MfLegendItem } from './board/MfLegend';
import { ResidentName, residentLabel, closedWords } from './board/residents';
import { ToneChip, WordChip } from './board/chips';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';

const ZONE = 'America/New_York';
const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ZONE }) : '—');
/** A YYYY-MM-DD date names a day, not an instant: read it as that day, never shifted by a zone. */
const day = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

/* ── R5: the lead-row disposition ───────────────────────────────────────── */

export function DispositionAction({ caseId, residentLabel, onDone }: {
  caseId: string; residentLabel: string; onDone: (message: string) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState<DispositionWord | ''>('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key, setKey] = useState(actKey());
  const chosen = DISPOSITION_WORDS.find((d) => d.word === word);
  const missing = Boolean(chosen?.reasonRequired && !reason.trim());
  const reasonId = `rs-why-${caseId}`;
  const errId = `rs-why-missing-${caseId}`;

  const reset = () => { setOpen(false); setWord(''); setReason(''); setTried(false); setErr(null); setKey(actKey()); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chosen) return;
    if (missing) { setTried(true); return; }
    setBusy(true); setErr(null);
    try {
      const r = await resubmittalsApi.disposition(caseId, { status: chosen.word, reason: reason.trim() || null, idempotency_key: key });
      if (!r.success || !r.data) { setErr(refusalWords(r, 'eMerge did not record this.')); return; }
      const carried = r.data.siblingsCarried?.length || 0;
      const parts = [`${residentLabel}: closed as ${chosen.label}.`];
      if (carried) parts.push(`${carried === 1 ? 'Their other open record was' : `Their ${carried} other open records were`} closed with it.`);
      if (r.data.resubmittal) parts.push(r.data.resubmittal.created ? 'Added to Resubmittals for the provider.' : 'Already on Resubmittals; not added twice.');
      if (r.data.verification) parts.push(r.data.verification.created ? 'Added to Needs verification for a VA.' : 'Already on Needs verification; not added twice.');
      reset();
      await onDone(parts.join(' '));
    } catch { setErr('Could not reach eMerge. Nothing was changed.'); } finally { setBusy(false); }
  };

  if (!open) {
    return <button type="button" className="rs-quiet" onClick={() => setOpen(true)} aria-label={`Disposition for ${residentLabel}`}>Disposition…</button>;
  }
  return (
    <form className="rs-dispo" onSubmit={submit} aria-label={`Disposition for ${residentLabel}`}>
      <label>
        <span>Close as</span>
        <select value={word} onChange={(e) => { setWord(e.target.value as DispositionWord | ''); setErr(null); }} aria-label="Disposition" disabled={busy}>
          <option value="">Choose…</option>
          {DISPOSITION_WORDS.map((d) => <option key={d.word} value={d.word}>{d.label}</option>)}
        </select>
      </label>
      <label>
        <span>{chosen?.reasonRequired ? 'Why (required)' : 'Why (optional)'}</span>
        <input id={reasonId} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} disabled={busy}
          aria-label="Disposition reason" aria-required={Boolean(chosen?.reasonRequired)}
          aria-invalid={tried && missing ? true : undefined} aria-describedby={tried && missing ? errId : undefined} />
      </label>
      {tried && missing && <p id={errId} className="rs-alert" role="alert">Say why. {chosen?.label} is a decision about the resident, and the reason stays on the record.</p>}
      {chosen && (
        <p className="rs-hint">
          Closes this lead and any other open record for the resident. It leaves the working views and stays findable in History and resident search.
          {chosen.toProvider && ' The resident goes on Resubmittals, once, for the provider.'}
          {chosen.toVerify && ' The resident goes on Needs verification, once, for a VA to check benefits.'}
        </p>
      )}
      <span className="rs-acts">
        <button type="submit" className="mf-go" disabled={busy || !chosen}>{busy ? 'Closing…' : 'Close the lead'}</button>
        <button type="button" className="mf-go ghost" onClick={reset} disabled={busy}>Cancel</button>
      </span>
      {err && <p className="rs-alert" role="alert">{err}</p>}
    </form>
  );
}

/* ── R4: the Resubmittals board ─────────────────────────────────────────── */

/** One resubmittal as a table row: the item, carrying its building's group facts. */
type RsRow = Resubmittal & { building: string; building_key: string; next_visit: NextVisit | null; provider: string | null };

/** The PA's own word for each reason code (a Monday word, drawn by WordChip). */
const REASON_WORD: Record<ResubmittalReason, string> = {
  REEVALUATE_CURRENT_YEAR: 'REEVALUATE - CURRENT YEAR',
  REEVALUATE_12_PLUS_MONTHS: 'REEVALUATE - 12+ MONTHS',
  RESUBMIT_TO_PROVIDER: 'RESUBMIT TO PROVIDER',
};
const REASONS = Object.keys(REASON_WORD) as ResubmittalReason[];
const REASON_MEANING: Record<ResubmittalReason, string> = {
  REEVALUATE_CURRENT_YEAR: 'The provider sees the resident again this year.',
  REEVALUATE_12_PLUS_MONTHS: 'The provider sees the resident again after 12 or more months.',
  RESUBMIT_TO_PROVIDER: 'The resident is resubmitted to the provider.',
};

const ReasonChip = ({ code }: { code: ResubmittalReason }) => (
  <WordChip word={REASON_WORD[code] || code} label={RESUBMITTAL_REASON_LABEL[code] || code} />
);

const NOT_REC = <span className="board-na">not recorded</span>;

/** "Casa Fixture: 2 marked added to the manifest · 1 already marked.": the same words the building block always said. */
async function markAdded(ids: string[], building: string, note: string): Promise<{ msg: string } | { err: string }> {
  try {
    const r = await resubmittalsApi.place(ids, note.trim() || null);
    if (!r.success || !r.data) return { err: refusalWords(r, 'eMerge did not record this.') };
    const d = r.data;
    const parts = [`${building}: ${d.placed.length} marked added to the manifest`];
    if (d.already.length) parts.push(`${d.already.length} already marked`);
    if (d.refused.length) parts.push(`${d.refused.length} cancelled earlier and left as they were`);
    if (d.notFound.length) parts.push(`${d.notFound.length} no longer on the list`);
    return { msg: `${parts.join(' · ')}.` };
  } catch { return { err: 'Could not reach eMerge. Nothing was changed.' }; }
}

function MarkOne({ row, onDone }: { row: RsRow; onDone: (msg: string) => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setErr(null);
    const r = await markAdded([row.id], row.building, note);
    setBusy(false);
    if ('err' in r) { setErr(r.err); return; }
    setNote('');
    onDone(r.msg);
  };
  return (
    <div className="mf-panel">
      <p>Add {residentLabel(row)} to {row.building}&apos;s manifest workbook by hand, then mark them added here. This screen writes nothing to SharePoint.</p>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Note (optional)" aria-label="Note for this resident" disabled={busy} />
      <div className="mf-acts">
        <button type="button" className="board-btn board-btn--primary" onClick={go} disabled={busy}>{busy ? 'Recording…' : 'Mark added to the manifest'}</button>
      </div>
      {err && <p className="mf-notice mf-notice--stop" role="alert">{err}</p>}
    </div>
  );
}

function CancelRow({ row, onDone }: { row: RsRow; onDone: (msg: string) => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const label = residentLabel(row);
  const errId = `rs-cancel-missing-${row.id}`;
  if (!open) return <button type="button" className="rs-quiet" onClick={() => setOpen(true)} aria-label={`Cancel the resubmittal for ${label}`}>Cancel</button>;
  return (
    <form className="rs-cancel" aria-label={`Cancel the resubmittal for ${label}`} onSubmit={async (e) => {
      e.preventDefault();
      if (!reason.trim()) { setTried(true); return; }
      setBusy(true); setErr(null);
      try {
        const r = await resubmittalsApi.cancel(row.id, reason.trim());
        if (!r.success) { setErr(refusalWords(r, 'eMerge did not cancel this.')); return; }
        onDone(`${label}: resubmittal cancelled.`);
      } catch { setErr('Could not reach eMerge. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} placeholder="Why it is cancelled" disabled={busy}
        aria-label="Why it is cancelled" aria-required aria-invalid={tried && !reason.trim() ? true : undefined} aria-describedby={tried && !reason.trim() ? errId : undefined} />
      <button type="submit" className="mf-go ghost" disabled={busy}>{busy ? 'Cancelling…' : 'Cancel it'}</button>
      <button type="button" className="rs-quiet" onClick={() => { setOpen(false); setReason(''); setTried(false); setErr(null); }} disabled={busy}>Keep it</button>
      {tried && !reason.trim() && <p id={errId} className="rs-alert" role="alert">Say why it is cancelled.</p>}
      {err && <p className="rs-alert" role="alert">{err}</p>}
    </form>
  );
}

/* ── Find any resident, and say where they stand (read-only) ───────────── */

const FIND_PLACE: Record<string, { label: string; href: string }> = {
  RESUBMITTAL_PENDING: { label: 'Resubmittals', href: '/portal/manifest?view=resubmittals' },
  OPEN_HELD: { label: 'All cases', href: '/portal/patient-advocate?view=all' },
  OPEN_UNASSIGNED: { label: 'Unassigned', href: '/portal/manifest?view=unassigned' },
  CLOSED: { label: 'All cases', href: '/portal/patient-advocate?view=all' },
  NO_CASE: { label: 'All cases', href: '/portal/patient-advocate?view=all' },
};

export function findStateWords(h: ResidentFindHit): string {
  const c = h.latest_case;
  switch (h.state) {
    case 'RESUBMITTAL_PENDING': return `On Resubmittals (pending since ${when(h.resubmittal_since)})`;
    case 'OPEN_HELD': return `Open lead held by ${c?.advocate || 'a Patient Advocate'}`;
    case 'OPEN_UNASSIGNED': return 'Open lead, nobody holds it';
    case 'CLOSED': {
      // A duplicate closed as "DUPLICATE OF <id>" says "duplicate", never the id.
      const { word } = closedWords(c?.closed_word);
      return `Closed ${c?.closed_on ? day(String(c.closed_on).slice(0, 10)) : 'date not recorded'}${word ? ` (${word})` : ''} — not on Resubmittals`;
    }
    default: return 'No case';
  }
}

function FindResident() {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<ResidentFindHit[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [paging, setPaging] = useState({ total: 0, page: 1, limit: 25 });
  const [more, setMore] = useState(false);
  const moreBusy = React.useRef(false);
  const seq = React.useRef(0);
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { seq.current += 1; moreBusy.current = false; setMore(false); setHits(null); setProblem(null); setPaging({ total: 0, page: 1, limit: 25 }); return undefined; }
    const mine = ++seq.current;
    moreBusy.current = false;
    setMore(false);
    // The old page count must not outlive the old query: Show more stays hidden until page 1 of this one arrives.
    setPaging({ total: 0, page: 1, limit: 25 });
    const t = setTimeout(async () => {
      try {
        const r = await manifestApi.findResident(term);
        if (mine !== seq.current) return;
        if (r.success && r.data) { setHits(r.data.items); setPaging({ total: r.data.total, page: 1, limit: r.data.limit || 25 }); setProblem(null); } else { setHits(null); setProblem(r.message || r.error || 'Could not search.'); }
      } catch { if (mine === seq.current) { setHits(null); setProblem('Could not reach eMerge.'); } }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const showMore = async () => {
    if (moreBusy.current) return;
    moreBusy.current = true;
    const mine = seq.current;
    setMore(true);
    try {
      const r = await manifestApi.findResident(q.trim(), paging.page + 1);
      if (mine !== seq.current) return;
      if (r.success && r.data) {
        const next = r.data.items;
        setHits((cur) => [...(cur || []), ...next]);
        setPaging({ total: r.data.total, page: paging.page + 1, limit: r.data.limit || paging.limit });
      } else setProblem(r.message || r.error || 'Could not load more.');
    } catch { if (mine === seq.current) setProblem('Could not reach eMerge.'); } finally { if (mine === seq.current) { moreBusy.current = false; setMore(false); } }
  };
  return (
    <div style={{ flex: '1 1 100%', minWidth: 0 }}>
      <input className="mf-filter" aria-label="Find any resident, and see where they stand" placeholder="Find any resident by name or CounselEAR id" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" />
      {problem && <p className="mf-note warn" role="alert">{problem}</p>}
      {hits && hits.length === 0 && <TideState kind="empty" reason="filtered" message={<>No resident matches “{q.trim()}”. Residents with no name on file can only be found by CounselEAR id.</>} />}
      {hits && hits.length > 0 && (
        <ul className="mf-leads" aria-label="Where each resident stands">
          {hits.map((h) => {
            const place = FIND_PLACE[h.state];
            return (
              <li key={h.id}>
                <strong>{h.name || 'Name not on file'}</strong>
                <span className="mf-sub"> {h.counselear_id ? `CounselEAR ${h.counselear_id} · ` : ''}<BuildingOrClinic row={h} fallback="No facility on file" /> · {findStateWords(h)} · </span>
                <a href={place.href}>{place.label}</a>
              </li>
            );
          })}
        </ul>
      )}
      {hits && hits.length > 0 && paging.total > hits.length && (
        <>
          <p className="mf-sub" role="status">Showing 1–{hits.length} of {paging.total}</p>
          <button type="button" className="mf-go ghost" disabled={more} onClick={showMore}>{more ? 'Loading…' : 'Show more'}</button>
        </>
      )}
      {hits && paging.total > paging.limit && <p className="mf-sub">Type more of the name, or the CounselEAR id, to narrow.</p>}
    </div>
  );
}

const SORTERS: Record<string, (r: RsRow) => string | number | null> = {
  building: (r) => r.building,
  asked: (r) => (r.requested_at ? new Date(r.requested_at).getTime() : null),
};

export default function ResubmittalsDesk() {
  const [data, setData] = useState<ResubmittalList | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useOpenParam('open');
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkNote, setBulkNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setProblem(null);
    try {
      const r = await resubmittalsApi.list();
      if (r.success && r.data) setData(r.data);
      else setProblem(refusalWords(r, 'Could not load the resubmittals.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // One row per resubmittal, in the server's order: its building's group, then its items.
  const all: RsRow[] = useMemo(() => (data ? data.groups.flatMap((g) => g.items.map((r) => ({
    ...r, building: g.facility_name || 'No confirmed building', building_key: g.facility_id || 'none', next_visit: g.next_visit, provider: g.provider,
  }))) : []), [data]);
  const canAct = Boolean(data?.can_act);
  const shown = useMemo(() => (legendKey ? all.filter((r) => r.reason_code === legendKey) : all), [all, legendKey]);
  const sort = useClientSort<RsRow>(shown, SORTERS);

  const done = (m: string) => { setNotice(m); setPicked(new Set()); setOpen(null); load(); };

  const selected = all.filter((r) => picked.has(r.id));
  const buildings = Array.from(new Set(selected.map((r) => r.building_key)));
  const oneBuilding = buildings.length === 1;
  const toggle = (id: string) => { setBulkErr(null); setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const everyShown = sort.rows.length > 0 && sort.rows.every((r) => picked.has(r.id));
  // Per building (as each building's block offered before 2026-10-07): "Select every resident at <building>" picks
  // exactly that building's shown residents, so the one-building rule below is met; pressed again, it lets them go.
  const byBuilding = useMemo(() => {
    const m = new Map<string, { key: string; name: string; ids: string[] }>();
    for (const r of shown) {
      const e = m.get(r.building_key);
      if (e) e.ids.push(r.id); else m.set(r.building_key, { key: r.building_key, name: r.building, ids: [r.id] });
    }
    return Array.from(m.values());
  }, [shown]);
  const toggleBuilding = (ids: string[]) => {
    setBulkErr(null);
    setPicked((prev) => (ids.every((id) => prev.has(id)) ? new Set(Array.from(prev).filter((id) => !ids.includes(id))) : new Set(ids)));
  };

  const markSelected = async () => {
    if (!selected.length || !oneBuilding) return;
    setBusy(true); setBulkErr(null);
    const r = await markAdded(selected.map((x) => x.id), selected[0].building, bulkNote);
    setBusy(false);
    if ('err' in r) { setBulkErr(r.err); return; }
    setBulkNote('');
    done(r.msg);
  };

  const legendItems: MfLegendItem[] = REASONS.map((code) => ({
    key: code, label: RESUBMITTAL_REASON_LABEL[code], sample: <ReasonChip code={code} />, meaning: REASON_MEANING[code],
    count: all.filter((r) => r.reason_code === code).length,
  }));

  const columns: BoardColumn<RsRow>[] = [
    ...(canAct ? [{
      key: 'pick', label: 'Select', help: 'Pick residents of one building to mark added to its manifest, above the table.',
      render: (r: RsRow) => <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${residentLabel(r)}`} />,
    }] : []),
    { key: 'building', label: 'Building', sort: 'building', help: 'The building the resident lives in now. The list is grouped by building, in the server\'s order, until you sort.', render: (r) => <span className="board-clip" title={r.building}><BuildingOrClinic row={r} fallback={r.building} /></span> },
    { key: 'visit', label: 'Next visit', help: 'Where the provider will see the building next, with "(confirmed)" when the visit is confirmed.', render: (r) => (r.next_visit ? <span>{day(r.next_visit.on)}{r.next_visit.state === 'CONFIRMED' ? ' (confirmed)' : ''}</span> : NOT_REC) },
    { key: 'provider', label: 'Provider', help: 'The provider for the building\'s next visit.', render: (r) => (r.provider ? <span className="board-clip" title={r.provider}>{r.provider}</span> : NOT_REC) },
    { key: 'reason', label: 'Reason', help: 'Why the PA sent the resident back to the provider, in the PA\'s own word.', render: (r) => <ReasonChip code={r.reason_code} /> },
    { key: 'by', label: 'Asked by', help: 'Who asked for the resubmittal.', render: (r) => (r.requested_by_name ? <span className="board-clip" title={r.requested_by_name}>{r.requested_by_name}</span> : NOT_REC) },
    { key: 'asked', label: 'Asked on', sort: 'asked', help: 'The day the resubmittal was asked for.', render: (r) => <span>{when(r.requested_at)}</span> },
    { key: 'pa', label: 'PA', help: 'The Patient Advocate whose case it came from.', render: (r) => (r.originating_advocate_name ? <span className="board-clip" title={r.originating_advocate_name}>{r.originating_advocate_name}</span> : NOT_REC) },
    { key: 'moved', label: 'Moved', help: 'Attention when the resident now lives in a different building from the one it was asked for at.', render: (r) => (r.moved_since ? <ToneChip tone="attn" title={r.requested_facility_name ? `Asked while at ${r.requested_facility_name}` : undefined}>Moved since asked</ToneChip> : <span className="board-na">not moved</span>) },
  ];

  const identity = {
    label: 'Resident',
    help: 'The resident to go back to the provider. Open the row for the details, to mark them added to the manifest, or to cancel the resubmittal.',
    render: (r: RsRow) => <ResidentName p={r} />,
    name: (r: RsRow) => residentLabel(r),
  };

  const sections = (r: RsRow): BoardDetailSection[] => {
    const out: BoardDetailSection[] = [];
    if (canAct) {
      out.push({ id: 'mark', label: 'Mark added', render: () => <MarkOne row={r} onDone={done} /> });
      out.push({
        id: 'cancel', label: 'Cancel',
        render: () => (
          <div className="mf-panel">
            <p>A resident who should not go back to the provider is cancelled, with a reason.</p>
            <div className="mf-acts"><CancelRow row={r} onDone={done} /></div>
          </div>
        ),
      });
    }
    out.push({
      id: 'details', label: 'Details',
      render: () => (
        <div className="mf-panel">
          <dl className="mf-facts">
            <dt>Reason</dt><dd>{RESUBMITTAL_REASON_LABEL[r.reason_code] || r.reason_code}</dd>
            <dt>Note</dt><dd>{r.reason_note || <span className="board-na">none</span>}</dd>
            <dt>Lives at</dt><dd><BuildingOrClinic row={r} fallback={r.building} /></dd>
            {r.moved_since && <><dt>Asked while at</dt><dd>{r.requested_facility_name || <span className="board-na">not recorded</span>}</dd></>}
            <dt>Closed by the PA</dt><dd>{r.originating_closed_on ? day(r.originating_closed_on.slice(0, 10)) : <span className="board-na">not recorded</span>}</dd>
          </dl>
        </div>
      ),
    });
    return out;
  };

  let state: React.ReactNode = null;
  if (problem) state = <MfLoadFailure message={problem} retry={load} />;
  else if (!data) state = <MfSkeleton bare />;
  else if (all.length === 0) state = <TideState kind="empty" reason="none" kindLabel="Nothing waiting" message="Nobody is waiting to go back to a provider." />;

  const controls = data ? (
    <>
      <FindResident />
      {canAct && all.length > 0 && (
        <div className="mf-bulk" role="group" aria-label="Mark the selection added">
          <label className="mf-check"><input type="checkbox" checked={everyShown} onChange={() => setPicked(everyShown ? new Set() : new Set(sort.rows.map((r) => r.id)))} aria-label={`Select all ${sort.rows.length} shown`} /> All {sort.rows.length} shown</label>
          <span className="mf-buildings-pick" role="group" aria-label="Select by building">
            {byBuilding.map((b) => {
              const allHere = b.ids.every((id) => picked.has(id));
              return (
                <label key={b.key} className="mf-check">
                  <input type="checkbox" checked={allHere} onChange={() => toggleBuilding(b.ids)} aria-label={`Select every resident at ${b.name}`} />
                  All at <span className="board-clip" title={b.name}>{b.name}</span> ({b.ids.length})
                </label>
              );
            })}
          </span>
          <input value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} maxLength={2000} placeholder="Note (optional)" aria-label="Note for the selection" disabled={busy} />
          <button type="button" className="board-btn board-btn--primary" onClick={markSelected} disabled={busy || selected.length === 0 || !oneBuilding}>
            {busy ? 'Recording…' : `Mark added to the manifest${selected.length ? ` (${selected.length})` : ''}`}
          </button>
          {selected.length > 0 && !oneBuilding && <span role="status">Select residents of one building</span>}
          {bulkErr && <span className="mf-notice mf-notice--stop" role="alert">{bulkErr}</span>}
        </div>
      )}
    </>
  ) : null;

  return (
    <main className="mf-main mf-main--board">
      <MfBoard<RsRow>
        title="Resubmittals"
        count={data ? all.length : null}
        units={['resident', 'residents']}
        what="Residents a Patient Advocate sent back to the provider. Add them to the building's manifest workbook, then mark them added here."
        notice={notice ? <p className="mf-notice" role="status">{notice}</p> : null}
        controls={controls}
        legend={data && all.length > 0 ? (
          <MfLegend title="Reason key" items={legendItems} whole={all.length} units={['resident', 'residents']} exclusive value={legendKey} onSelect={setLegendKey}
            scope="Each resident is here for one reason, in the PA's own word. Press a reason to show only those residents." />
        ) : null}
        state={state}
        columns={columns}
        identity={identity}
        rows={sort.rows}
        rowKey={(r) => r.id}
        openKey={open}
        onOpenChange={setOpen}
        detailSections={sections}
        detailLabel={(r) => `${residentLabel(r)}: the resubmittal`}
        sort={sort.sort}
        dir={sort.dir}
        sorts={sort.sorts}
        onSort={sort.onSort}
        foot={data ? (
          <>
            {!data.can_act && data.total > 0 && <p>You can see this list; marking residents added needs the lead-placement permission.</p>}
            {data.total !== all.length && <p>The server counted {data.total} but sent {all.length}; the table shows the {all.length} it holds.</p>}
          </>
        ) : null}
        notes={['"Name not recorded": eMerge holds no name for this resident yet; CounselEAR sends the name after the resident.']}
      />
    </main>
  );
}
